import {
  BLOOD_TEST_STATUS_LABELS,
  COMPLETION_ITEM_LABEL,
  COMPLETION_ITEM_SOURCE_LABEL,
  completionProgress,
  completionProgressLabel,
  completionRoundProgress,
  type ClinicianMedicalEvidence,
  type CompletionItem,
  type CompletionRound,
  type CompletionRoundItem,
} from "@care-by-ella/contracts";
import type { StatusTone } from "@care-by-ella/ui-tokens";

// TES-1060 — the round whose Kompletteringsanteckning is shown: the newest
// one where something has come in. TES-1242 — shared by the Blodprov tab and
// the transfer review, which shows the same note when the case comes back.
export function completionNoteRoundId(
  d: Pick<ClinicianMedicalEvidence, "rounds"> | null | undefined,
): string | null {
  return (
    d?.rounds?.find((r) =>
      r.items.some((i) => i.status === "done" || i.status === "waived"),
    )?.id ?? null
  );
}

// TES-1059 — the round "Kompletteringar: n av m klara" is read from: the
// open or ready one on the case the checklist is showing.
export function currentRound(
  d: Pick<ClinicianMedicalEvidence, "rounds" | "meetingDocumentedAt">,
): CompletionRound | null {
  if (!d.meetingDocumentedAt) return null;
  const opened = new Date(d.meetingDocumentedAt).getTime();
  return (
    d.rounds?.find(
      (r) =>
        (r.status === "open" || r.status === "ready") &&
        new Date(r.requestedAt).getTime() === opened,
    ) ?? null
  );
}

// "Kompletteringar: n av m klara" for the patient's open completion: from
// the stored round when the case has one, else from the same checklist the
// app shows her. A ready round still shows (her part is done, the doctor's
// review is not), even where her checklist has already closed. Null when no
// completion is open.
export function completionProgressFor(
  d: Pick<
    ClinicianMedicalEvidence,
    "active" | "tasks" | "customTasks" | "rounds" | "meetingDocumentedAt"
  >,
): string | null {
  const round = currentRound(d);
  if (round) {
    return completionProgressLabel(completionRoundProgress(round.items));
  }
  if (!d.active) return null;
  return d.tasks.length > 0 || (d.customTasks?.length ?? 0) > 0
    ? completionProgressLabel(completionProgress(d.tasks, d.customTasks ?? []))
    : null;
}

const STOCKHOLM_TIME = new Intl.DateTimeFormat("sv-SE", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Stockholm",
});

// One line of the completion history: what was asked for, and for a done
// item where it came from and when.
export function completionItemLine(i: CompletionRoundItem): {
  label: string;
  state: string;
} {
  const status =
    i.status === "requested"
      ? "Saknas"
      : i.status === "waived"
        ? "Behövs inte längre"
        : "Klar";
  const parts = [status];
  if (i.status !== "requested" && i.source) {
    parts.push(COMPLETION_ITEM_SOURCE_LABEL[i.source]);
  }
  if (i.status !== "requested" && i.doneAt) {
    parts.push(STOCKHOLM_TIME.format(new Date(i.doneAt)));
  }
  return { label: i.label, state: parts.join(" · ") };
}

// TES-1329 (TES-1327 §5) — "Begärda kompletteringar" on the patient overview:
// "Här visas bara sådant som Kirill aktivt har begärt efter vårdmötet eller efter bedömningen av ett receptövertag"
// "Varje komplettering ska visas separat med sin egen status."
// So one row per item the doctor asked for in the open (or ready) round, with
// that item's own status, and nothing derived from what she merely has not
// handed over.
export interface RequestedCompletionRow {
  key: string;
  label: string;
  tone: StatusTone;
  state: string;
  // The case's blood-test status under the blood-test item.
  detail: string | null;
}

const ITEM_TONE: Record<CompletionRoundItem["status"], StatusTone> = {
  requested: "missing",
  done: "complete",
  waived: "neutral",
};

// A sample given or a result in that no doctor has reviewed yet: never
// hidden, even when the item that asked for it reads done.
function unreviewedBlood(
  d: Pick<ClinicianMedicalEvidence, "bloodTest">,
): string | null {
  const s = d.bloodTest?.status;
  return s === "sample_reported" || s === "result_registered"
    ? BLOOD_TEST_STATUS_LABELS[s]
    : null;
}

export function requestedCompletionRows(
  d: Pick<
    ClinicianMedicalEvidence,
    "active" | "tasks" | "customTasks" | "transferCompletion" | "bloodTest"
  > &
    Pick<Partial<ClinicianMedicalEvidence>, "rounds">,
): RequestedCompletionRow[] {
  const blood = unreviewedBlood(d);
  const open = (d.rounds ?? []).filter(
    (r) => r.status === "open" || r.status === "ready",
  );
  const rows: RequestedCompletionRow[] = open.flatMap((r) =>
    r.items.map((i) => ({
      key: i.id,
      label: completionItemLine(i).label,
      tone: ITEM_TONE[i.status],
      state: completionItemLine(i).state,
      detail: i.key === "new_blood_test" ? blood : null,
    })),
  );
  // A case opened before rounds were stored (TES-1059) has its checklist
  // only: the same rows from there.
  if (open.length === 0 && d.active) {
    const status = (done: boolean) => ({
      tone: (done ? "complete" : "missing") as StatusTone,
      state: done ? "Klar" : "Saknas",
    });
    for (const t of d.tasks) {
      rows.push({
        key: t.key,
        label:
          t.key === "lab_payment"
            ? "Betala blodprovet"
            : COMPLETION_ITEM_LABEL[t.key as CompletionItem],
        ...status(t.status === "done"),
        detail: t.key === "new_blood_test" ? blood : null,
      });
    }
    if (d.transferCompletion) {
      rows.push({
        key: "transfer_f07",
        label: "Komplettera formuläret för receptövertag",
        ...status(d.transferCompletion.status === "done"),
        detail: null,
      });
    }
    for (const c of d.customTasks ?? []) {
      rows.push({
        key: c.id,
        label: c.heading || COMPLETION_ITEM_LABEL.other,
        ...status(c.status === "done"),
        detail: null,
      });
    }
  }
  // The never-drop rule: an unreviewed sample or result with no blood-test
  // row to sit under gets a row of its own.
  if (blood && !rows.some((r) => r.detail === blood)) {
    rows.push({
      key: "blood_test",
      label: "Blodprov",
      tone: "incomplete",
      state: blood,
      detail: null,
    });
  }
  return rows;
}
